import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormField,
  Input,
  Link,
  Pagination,
  SpaceBetween,
  Spinner,
  Table,
} from "@cloudscape-design/components";
import { Link as RouterLink } from "react-router-dom";
import { apiFetch } from "../apiFetch";
import type { ParamFilter, TableColumn, TableWidget, Widget } from "./viewContract";
import { readField } from "./viewContract";
import { buildFetchQuery, toRecordPath as toRecordPathFor } from "./widgetData";
import { useWidgetRenderContext } from "./widgetRegistry";

/**
 * Renders a single SELF-CONTAINED `table` widget (render-pipeline stage 4).
 *
 * Given its own widget node + the shared schema context, this component:
 *  - resolves its OWN collection uid -> REST pluralName and short name,
 *  - builds its OWN Strapi 5 query (indexed `fields[]` for main scalars, nested
 *    `populate[rel][fields][]` for one-hop relation paths, `filters[field][$contains]`
 *    for applied viewer filters, and `pagination[page]/[pageSize]`),
 *  - fetches and manages its OWN items / pageCount / currentPage / filterInputs /
 *    appliedFilters state, with SCOPED loading + error (a failing table never breaks
 *    a sibling widget),
 *  - renders the filter bar (one Input per filterableField + Apply + Enter-to-apply +
 *    Clear), the Table, and server-side Pagination.
 *
 * All Phase-1 table behaviors are preserved: relation one-hop fields (prefix-strip
 * read), contextLink links with the XSS allowlist, server-side pagination
 * (meta.pagination.pageCount), viewer filters with explicit Apply (no debounce; apply
 * on button/Enter; Clear resets; page resets to 1 on apply/clear), scoped unknown-column
 * Alert, scoped fetch-error Alert.
 *
 * STABLE IDENTITY: this component keeps a stable element identity across its own data
 * refetches (filterInputs/appliedFilters/currentPage are LOCAL state, so refetching
 * does not change this element's key), so the filter Inputs reconcile rather than
 * remount and keep focus/caret while typing.
 */

/**
 * Build the Strapi 5 filter params from the viewer's APPLIED filter values. Only the
 * widget's declared `filterableFields` are considered, and only those whose applied
 * value is non-empty (trimmed) contribute `filters[<field>][$contains]=<value>`. Strapi
 * ANDs separate top-level `filters[...]` params. The operator is always `$contains`.
 */
function buildViewerFilterQuery(filterableFields: string[], applied: Record<string, string>): string {
  const parts: string[] = [];
  for (const field of filterableFields) {
    const value = (applied[field] ?? "").trim();
    if (value === "") continue;
    parts.push(`filters[${encodeURIComponent(field)}][$contains]=${encodeURIComponent(value)}`);
  }
  return parts.join("&");
}

/**
 * Build the Strapi 5 filter params from the view's PARAM-BOUND filters. For each
 * `paramFilters` entry, look up its bound param by name in the URL `urlParams` map; if the
 * value is PRESENT and NON-EMPTY, append an INVISIBLE exact-match `filters[<field>][$eq]`.
 * If the param is absent/empty, the filter is simply OMITTED. `field` is a main-collection
 * scalar or "documentId" — never a relation path, so no nesting is emitted. These params
 * AND together with the viewer `$contains` filters and pagination in the SAME query.
 */
function buildParamFilterQuery(
  paramFilters: ParamFilter[],
  urlParams: Record<string, string>
): string {
  const parts: string[] = [];
  for (const pf of paramFilters) {
    const value = (urlParams[pf.param] ?? "").trim();
    if (value === "") continue;
    parts.push(`filters[${encodeURIComponent(pf.field)}][$eq]=${encodeURIComponent(value)}`);
  }
  return parts.join("&");
}

// XSS allowlist: only internal app paths ("/..." but not protocol-relative "//") or
// absolute http(s) URLs are allowed. Everything else (javascript:, data:, vbscript:,
// other schemes) is rejected. The test runs on the FINAL resolved string, trimmed,
// case-insensitively, and any control character disqualifies it outright.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;
function isAllowedHref(href: string): "internal" | "external" | null {
  const trimmed = href.trim();
  if (!trimmed || CONTROL_CHARS.test(trimmed)) return null;
  if (/javascript:/i.test(trimmed)) return null;
  if (trimmed.startsWith("//")) return null; // protocol-relative
  if (trimmed.startsWith("/")) return "internal";
  if (/^https?:\/\//i.test(trimmed)) return "external";
  return null;
}

export default function TableWidgetView({ widget }: { widget: Widget }) {
  // Narrow to the table shape. The registry only routes `type === "table"` here.
  const table = widget as TableWidget;
  const { schema, pluralNameFor, urlParams } = useWidgetRenderContext();

  // --- Resolve this table's OWN collection uid -> plural + short name -----------------
  const resolvedPlural = pluralNameFor(table.collection);
  const mainCt = schema.find((c) => c.uid === table.collection);
  const collectionShortName = mainCt?.apiName ?? table.collection.split(".").pop() ?? "";

  // --- Scoped data lifecycle state ----------------------------------------------------
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [items, setItems] = useState<Record<string, any>[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  // VIEWER-INTERACTIVE filters (component-local; NEVER persisted). `filterInputs` is the
  // immediate controlled value behind each Input; `appliedFilters` is the committed copy
  // that actually drives the fetch. Typing updates only `filterInputs`.
  const [filterInputs, setFilterInputs] = useState<Record<string, string>>({});
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string>>({});

  const filterableFields = table.filterableFields ?? [];
  const paramFilters = table.paramFilters ?? [];

  // Update one filterable field's immediate value — does NOT fetch.
  const onFilterInputChange = useCallback((field: string, value: string) => {
    setFilterInputs((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Apply current inputs -> appliedFilters and reset to page 1; the data effect refetches.
  const onApplyFilters = useCallback(() => {
    setCurrentPage(1);
    setFilterInputs((inputs) => {
      setAppliedFilters({ ...inputs });
      return inputs;
    });
  }, []);

  // Clear inputs + applied filters, reset to page 1, refetch unfiltered.
  const onClearFilters = useCallback(() => {
    setCurrentPage(1);
    setFilterInputs({});
    setAppliedFilters({});
  }, []);

  // --- Data effect: fetch this table's own records. Keyed on resolved plural + its own
  // binding + currentPage + appliedFilters. Guarded so it no-ops until plural resolves.
  const relationsKey = table.relations.join("|");
  const selectedKey = table.selected.join("|");
  const columnsPageSize = table.pageSize ?? 10;
  const appliedKey = JSON.stringify(appliedFilters);
  // Stable dep key over ONLY the url param VALUES this widget references (by bound param
  // name). Navigating to a different ?param= value changes this key and refetches; changes
  // to unrelated query params do not. Also captures param-binding config changes.
  const paramFilterKey = JSON.stringify(
    paramFilters.map((pf) => [pf.field, pf.param, urlParams[pf.param] ?? ""])
  );

  useEffect(() => {
    if (!resolvedPlural) {
      // Nothing to fetch — the render below shows the scoped "content-type missing" Alert.
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const fieldsQuery = buildFetchQuery(table.selected, table.relations, collectionShortName);
        const filterQuery = buildViewerFilterQuery(filterableFields, appliedFilters);
        const paramFilterQuery = buildParamFilterQuery(paramFilters, urlParams);
        const paginationQuery = `pagination[page]=${currentPage}&pagination[pageSize]=${columnsPageSize}`;
        const query = [fieldsQuery, filterQuery, paramFilterQuery, paginationQuery].filter(Boolean).join("&");

        const dataRes = await apiFetch(`/${resolvedPlural}?${query}`);
        if (!dataRes.ok) {
          if (!cancelled) {
            setFetchError(`Failed to load data from "${resolvedPlural}" (${dataRes.status}).`);
            setLoading(false);
          }
          return;
        }
        const dataJson = await dataRes.json();
        const records: Record<string, any>[] = Array.isArray(dataJson?.data) ? dataJson.data : [];
        if (!cancelled) {
          setItems(records);
          setPageCount(dataJson?.meta?.pagination?.pageCount ?? 1);
        }
      } catch (e) {
        if (!cancelled) setFetchError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedPlural, collectionShortName, relationsKey, selectedKey, columnsPageSize, currentPage, appliedKey, paramFilterKey]);

  // --- Render-time validation: unknown collection ------------------------------------
  if (!resolvedPlural) {
    return (
      <Alert type="error" header="Table widget error">
        The content-type "{table.collection || "(empty)"}" does not exist.
      </Alert>
    );
  }

  // --- Render-time validation: columns must reference selected fields ----------------
  const unknownColumns = table.columns.filter((c) => !table.selected.includes(c.field));
  if (unknownColumns.length > 0) {
    const names = unknownColumns.map((c) => `"${c.field}"`).join(", ");
    return (
      <Alert type="error" header="Table widget error">
        Column field(s) {names} are not part of this table's selected fields. Add them to the
        widget's fields or fix the column mapping.
      </Alert>
    );
  }

  // A stored relation path is "<mainShort>.<relation>.<field>" but the Strapi record is
  // shaped "<relation>.<field>" (no main-short prefix). Strip the leading main-short
  // segment before dot-reading; plain (single-segment) fields are read as-is.
  const toRecordPath = (field: string) => toRecordPathFor(field, collectionShortName);

  const cellValue = (item: Record<string, any>, field: string): string => {
    const value = readField(item, toRecordPath(field));
    if (value == null) return "-";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  // Resolve a contextLink template for one row: substitute every {token}. A token that is
  // one of this table's selected fields takes that row's value (URL-encoded); an unknown
  // token becomes the literal "errorfield".
  const resolveHref = (template: string, item: Record<string, any>): string =>
    template.replace(/\{([^}]*)\}/g, (_match, token: string) => {
      if (!table.selected.includes(token)) return "errorfield";
      const raw = readField(item, toRecordPath(token));
      const str = raw == null ? "" : typeof raw === "object" ? JSON.stringify(raw) : String(raw);
      return encodeURIComponent(str);
    });

  const columnDefinitions = table.columns.map((col: TableColumn, index: number) => ({
    id: `${col.field}-${index}`,
    header: col.header,
    cell: (item: Record<string, any>) => {
      const text = cellValue(item, col.field);
      const template = col.contextLink;
      if (!template || !template.trim()) return text;

      const href = resolveHref(template, item);
      const kind = isAllowedHref(href);
      if (kind === null) return text; // rejected href -> plain text, never a link
      if (kind === "internal") return <RouterLink to={href.trim()}>{text}</RouterLink>;
      return (
        <Link href={href.trim()} external>
          {text}
        </Link>
      );
    },
  }));

  return (
    <SpaceBetween size="m">
      {fetchError && (
        <Alert type="error" header="Failed to load table data">
          {fetchError}
        </Alert>
      )}
      {filterableFields.length > 0 && (
        <SpaceBetween direction="horizontal" size="s">
          {filterableFields.map((field) => (
            <FormField key={`filter-${field}`} label={field}>
              <Input
                type="search"
                value={filterInputs[field] ?? ""}
                placeholder={`Filter by ${field}`}
                onChange={({ detail }) => onFilterInputChange(field, detail.value)}
                onKeyDown={({ detail }) => {
                  if (detail.key === "Enter") onApplyFilters();
                }}
              />
            </FormField>
          ))}
          <FormField label="&nbsp;">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={onApplyFilters}>Apply filters</Button>
              <Button variant="link" onClick={onClearFilters}>
                Clear
              </Button>
            </SpaceBetween>
          </FormField>
        </SpaceBetween>
      )}
      {loading ? (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
        </Box>
      ) : (
        <Table
          variant="embedded"
          columnDefinitions={columnDefinitions}
          items={items}
          empty="No records found."
        />
      )}
      {pageCount > 1 && (
        <Pagination
          currentPageIndex={currentPage}
          pagesCount={pageCount}
          onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
        />
      )}
    </SpaceBetween>
  );
}
